import React, { useState } from 'react'
import { Modal } from 'react-bootstrap'

import { convertUTCtoMSK } from '../../../../../utils'
import { releaseDepositChunk } from '../../core/_requests'

interface Props {
  show: boolean
  onClose: () => void
  /** Токен спорного депозита — выплату держит именно он. */
  depositToken: string
  /**
   * Текущий момент освобождения: `null` — держим бессрочно. Показываем остаток, потому что новое
   * значение задаёт срок ЗАНОВО, а не прибавляется к нему — не видя текущего, оператор не знает,
   * укорачивает он удержание или продлевает.
   */
  heldUntil?: string | null
  /**
   * Строка списка обновляется вызывающим: у диспутов и у пополнений разные источники данных.
   * Аргумент — момент освобождения, который мэтчинг принял (ISO, UTC): в `held_until` уходит
   * ровно отправленное значение, поэтому строку можно обновить на месте, не перезапрашивая список.
   * Вызывается уже после `onClose`, так что окно к этому времени размонтировано.
   */
  onReleased?: (heldUntil: string) => void
}

const QUICK_MINUTES = [10, 20, 30, 60]

/**
 * Освобождение выплаты, которую держит спорный депозит (CPAYMENT-1857).
 *
 * Пока по депозиту открыт спор, выплата занята и не принимает новые пополнения — до момента
 * освобождения. Момент выставляется при создании диспута из периода охлаждения в профиле мэтчинга,
 * а если оператор отметил «не освобождать чанк», не выставляется вовсе: тогда отпустить выплату
 * можно только отсюда.
 *
 * Оператор выбирает «сейчас» или задержку в минутах, а не дату: решение принимается по ходу
 * разбора спора («дай ещё полчаса»), и календарь здесь только повод промахнуться годом. Отложенное
 * освобождение отзываемо — пока момент не наступил, действие остаётся в меню.
 */
export const ReleaseDepositChunkModal: React.FC<Props> = ({
  show,
  onClose,
  depositToken,
  heldUntil = null,
  onReleased,
}) => {
  const [minutes, setMinutes] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const at = new Date(Date.now() + minutes * 60 * 1000).toISOString()
      const response = await releaseDepositChunk(depositToken, at)
      // `success` здесь всегда true — это статус запроса. Освободилось ли что-то, говорит
      // `released`: мэтчинг отвечает `false`, когда освобождать нечего (спор уже закрыт коллегой,
      // депозит не проходил через мэтчинг). Показать успех в этом случае — соврать оператору.
      if (response.data?.success && response.data?.released) {
        onClose()
        onReleased?.(at)
        return
      }
      setError(
        response.data?.success
          ? 'Освобождать нечего: спор уже закрыт либо у пополнения нет связанной выплаты'
          : 'Не удалось освободить депозит'
      )
    } catch {
      setError('Не удалось освободить депозит')
    }

    // Только для оставшегося открытым окна: успешная ветка выше уже вышла из функции, и её
    // владелец мог перечитать список — окна к этому моменту нет.
    setSubmitting(false)
  }

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Освободить депозит</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className='text-muted fs-7'>
          Выплата перестанет ждать это пополнение и снова начнёт принимать другие — сразу или через
          указанное время.
        </p>

        <div className='notice d-flex bg-light-primary rounded border-primary border border-dashed p-3 mb-4'>
          <div className='fs-7 text-gray-700'>
            {heldUntil === null ? (
              <>Сейчас выплата удерживается <b>бессрочно</b> — до ручного освобождения.</>
            ) : (
              <>
                Сейчас выплата освободится <b>{describeRemaining(heldUntil)}</b> (
                {convertUTCtoMSK(heldUntil)} МСК).
              </>
            )}
            <div className='mt-1'>Новое значение задаёт срок заново, отсчёт — от текущего момента.</div>
          </div>
        </div>

        <label className='form-label required'>Когда освободить</label>
        <div className='d-flex flex-wrap gap-2 mb-3'>
          <button
            type='button'
            className={`btn btn-sm ${minutes === 0 ? 'btn-primary' : 'btn-light'}`}
            onClick={() => setMinutes(0)}
          >
            Сейчас
          </button>
          {QUICK_MINUTES.map((value) => (
            <button
              key={value}
              type='button'
              className={`btn btn-sm ${minutes === value ? 'btn-primary' : 'btn-light'}`}
              onClick={() => setMinutes(value)}
            >
              через {value} мин
            </button>
          ))}
        </div>

        <div className='d-flex align-items-center gap-2'>
          <span className='text-muted fs-7'>или через</span>
          {/*
            type='text' + inputMode: type='number' пускает минус и отдаёт пустую строку на
            частично введённом значении — тот же приём, что в формах риск-балла.
          */}
          <input
            type='text'
            inputMode='numeric'
            className='form-control form-control-sm'
            style={{ maxWidth: 90 }}
            value={minutes === 0 ? '' : String(minutes)}
            placeholder='0'
            onChange={(e) => {
              const raw = e.target.value
              if (raw !== '' && !/^\d{1,4}$/.test(raw)) {
                return
              }
              setMinutes(raw === '' ? 0 : Number(raw))
            }}
          />
          <span className='text-muted fs-7'>мин</span>
        </div>

        {error && <div className='text-danger mt-3'>{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <button type='button' className='btn btn-light' onClick={onClose} disabled={submitting}>
          Отмена
        </button>
        <button
          type='button'
          className='btn btn-primary'
          onClick={handleSubmit}
          disabled={submitting}
        >
          {minutes === 0 ? 'Освободить сейчас' : `Освободить через ${minutes} мин`}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

/** «через 12 мин» / «менее чем через минуту» — остаток до текущего момента освобождения. */
function describeRemaining(heldUntil: string): string {
  const minutes = Math.round((new Date(heldUntil).getTime() - Date.now()) / 60000)

  if (Number.isNaN(minutes)) {
    return 'в назначенный момент'
  }

  return minutes < 1 ? 'менее чем через минуту' : `через ${minutes} мин`
}

export default ReleaseDepositChunkModal
